export function createSchemaTools(site, faqData) {
  const getCanonicalPath = (pathname) => (pathname === "/home" ? "/" : pathname);

  const pageUrlForPath = (pathname) => {
    const canonicalPath = getCanonicalPath(pathname);
    return `${site.siteUrl}${canonicalPath === "/" ? "" : canonicalPath}`;
  };

  const areaServed = () => [
    ...site.counties.map((name) => ({ "@type": "AdministrativeArea", name })),
    ...site.areas.flatMap((area) =>
      area.cities.map((name) => ({
        "@type": "City",
        name,
        containedInPlace: { "@type": "AdministrativeArea", name: area.name },
      }))
    ),
  ];

  function buildLocalBusiness() {
    return {
    "@type": "LocalBusiness",
    "@id": `${site.siteUrl}/#localbusiness`,
    name: site.name,
    url: site.siteUrl,
    image: `${site.siteUrl}${site.logo}`,
    logo: `${site.siteUrl}${site.logo}`,
    description: site.description,
    telephone: site.phoneE164,
    email: site.email,
    address: {
      "@type": "PostalAddress",
      addressLocality: site.businessLocality,
      addressRegion: site.businessRegion,
      addressCountry: site.businessCountry,
    },
    hasMap: site.googleBusinessUrl,
    contactPoint: {
      "@type": "ContactPoint",
      telephone: site.phoneE164,
      email: site.email,
      contactType: "customer service",
      areaServed: "US",
    },
    areaServed: areaServed(),
    openingHoursSpecification: site.openingHours.map((hours) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: hours.days,
      opens: hours.opens,
      closes: hours.closes,
    })),
    sameAs: site.socials,
    knowsAbout: [...site.services.map((service) => service.name), ...site.additionalServices],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Cleaning Services",
      itemListElement: [
        ...site.services.map((service) => ({
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: service.name,
            url: `${site.siteUrl}${service.path}`,
          },
        })),
        ...site.additionalServices.map((name) => ({
          "@type": "Offer",
          itemOffered: { "@type": "Service", name },
        })),
      ],
    },
    };
  }

  function buildSchema(page, pathname = page?.path) {
    if (!page || !pathname) return null;

    const pageUrl = pageUrlForPath(pathname);
    const localBusiness = buildLocalBusiness();
    const graph = [
    localBusiness,
    {
      "@type": "WebSite",
      "@id": `${site.siteUrl}/#website`,
      url: site.siteUrl,
      name: site.name,
      publisher: { "@id": `${site.siteUrl}/#localbusiness` },
    },
    {
      "@type": "WebPage",
      "@id": `${pageUrl}#webpage`,
      url: pageUrl,
      name: page.title,
      description: page.description,
      isPartOf: { "@id": `${site.siteUrl}/#website` },
      about: { "@id": `${site.siteUrl}/#localbusiness` },
    },
    ];

    if (page.type === "service") {
      graph.push({
      "@type": "Service",
      "@id": `${pageUrl}#service`,
      name: page.service,
      serviceType: page.service,
      provider: { "@id": `${site.siteUrl}/#localbusiness` },
      areaServed: localBusiness.areaServed,
      url: pageUrl,
      });
      graph.push({
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: site.siteUrl },
        { "@type": "ListItem", position: 2, name: page.service, item: pageUrl },
      ],
      });
    }

    if (page.type === "area") {
      const area = site.areas.find((item) => item.slug === page.area);
      graph.push({
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: site.siteUrl },
        {
          "@type": "ListItem",
          position: 2,
          name: "Service Areas",
          item: `${site.siteUrl}/service-areas`,
        },
        { "@type": "ListItem", position: 3, name: area.name, item: pageUrl },
      ],
      });
    }

    if (page.type === "areas") {
      graph.push({
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: site.siteUrl },
        { "@type": "ListItem", position: 2, name: "Service Areas", item: pageUrl },
      ],
      });
    }

    if (page.type === "faq") {
      graph.push({
      "@type": "FAQPage",
      "@id": `${pageUrl}#faq`,
      mainEntity: faqData.flatMap((category) =>
        category.questions.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: { "@type": "Answer", text: faq.answer },
        }))
      ),
      });
    }

    return { "@context": "https://schema.org", "@graph": graph };
  }

  return {
    areaServed,
    buildLocalBusiness,
    buildSchema,
    getCanonicalPath,
    pageUrlForPath,
  };
}
